import { get } from "lodash";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "shared/hooks/useApi";
import client from "shared/graphql";
import { StoreContext } from "shared/store";
import {
  NOTIFICATIONS,
  UNREAD_NOTIFICATIONS_COUNT,
} from "utils/api/graphql/queries/notifications";
import { COUNT_TASKS, TASKS, TASK } from "utils/api/graphql/queries/tasks";
import { UPDATE_NOTIFICATION } from "utils/api/graphql/mutations/notifications";
import {
  CANCEL_TASK_REMINDER,
  REACTIVATE_TASK_REMINDER,
} from "utils/api/graphql/mutations/task";
import {
  INITIAL_VALUES,
  NOTIFICATION_TYPES,
  SCREENS,
  TASK_STATUSES,
  TASK_TYPES,
} from "utils/constants";
import View from "./View";
import TaskDetailsDrawer from "./Widgets/TaskDetailsDrawer";
import { withTaskSearchVars } from "./taskSearch";

const List = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const taskIdFromUrl = searchParams.get("task");
  const { user } = useContext(StoreContext);
  const [pagination, setPagination] = useState({});
  const [filter, setFilter] = useState(INITIAL_VALUES);
  const [openDetail, setOpenDetail] = useState(false);

  const { data: taskData } = useQuery(TASK, {
    variables: { where: { id: taskIdFromUrl } },
    skip: !taskIdFromUrl,
  });

  const { data: taskReminderNotificationsData } = useQuery(NOTIFICATIONS, {
    variables: {
      where: { type: NOTIFICATION_TYPES.TASK_REMINDER },
      take: 1000,
    },
    fetchPolicy: "cache-and-network",
  });

  const unreadReminderTaskIds = useMemo(() => {
    const list = get(taskReminderNotificationsData, "notifications.data", []);
    const readByCurrentUser = (notification) =>
      (get(notification, "isReadBy") || []).some((u) => u.id === user?.id);
    return new Set(
      list
        .filter((n) => !readByCurrentUser(n))
        .map((n) => get(n, "meta.taskId"))
        .filter(Boolean),
    );
  }, [taskReminderNotificationsData, user?.id]);
  // Combine all count queries into a single useQuery to minimize network requests
  const countsQueryVars = [
    { variables: {} }, // totalCount
    {
      variables: { where: { type: TASK_TYPES.AUTOMATIC } },
    },
    { variables: { where: { status: TASK_STATUSES.IN_PROGRESS } } }, // toDoCount
    { variables: { where: { status: TASK_STATUSES.COMPLETED } } }, // completedCount
    { variables: { where: { project: { author: null } } } }, // notAssignedCount
  ];

  const [counts, setCounts] = useState({
    totalCount: 0,
    automaticCount: 0,
    toDoCount: 0,
    completedCount: 0,
    notAssignedCount: 0,
  });

  const fetchCounts = useCallback(async () => {
    const results = await Promise.all(
      countsQueryVars.map((vars) =>
        client.query({
          query: COUNT_TASKS,
          fetchPolicy: "network-only",
          variables: withTaskSearchVars(vars.variables, filter),
        }),
      ),
    );
    setCounts({
      totalCount: results[0]?.data?.countTasks ?? 0,
      automaticCount: results[1]?.data?.countTasks ?? 0,
      toDoCount: results[2]?.data?.countTasks ?? 0,
      completedCount: results[3]?.data?.countTasks ?? 0,
      notAssignedCount: results[4]?.data?.countTasks ?? 0,
    });
  }, [filter]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  useEffect(() => {
    const handleTaskCreated = () => fetchCounts();
    window.addEventListener("taskCreated", handleTaskCreated);
    return () => window.removeEventListener("taskCreated", handleTaskCreated);
  }, [fetchCounts]);

  useEffect(() => {
    const task = get(taskData, "task");
    if (task && taskIdFromUrl) {
      setOpenDetail(task);
      setSearchParams({}, { replace: true });
    }
  }, [taskData, taskIdFromUrl, setSearchParams]);

  const {
    totalCount,
    automaticCount,
    toDoCount,
    completedCount,
    notAssignedCount,
  } = counts;

  const onSearch = ({ accessor, value }) => {
    setPagination((prev) => ({ ...prev, skip: 0 }));

    setFilter((prev) => {
      const keys = accessor.split(".");
      const next = { ...prev };

      let current = next;
      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        current[key] = { ...(current[key] || {}) };
        current = current[key];
      }
      current[keys[keys.length - 1]] = value;
      return next;
    });
  };
  const onDateSearch = (dates) => {
    setPagination((prev) => ({ ...prev, skip: 0 }));

    setFilter((prev) => ({
      ...prev,
      lt: {
        event: {
          startDate: dates[1],
        },
      },
      gt: {
        event: {
          startDate: dates[0],
        },
      },
    }));
  };
  const onOpenDetail = (id) => {
    setOpenDetail(id);
  };

  const onClose = () => {
    setOpenDetail(false);
  };

  const onDelete = (id) => {
    navigate(`${SCREENS.PRIVATE.BROKER.TASKS.DELETE.path}/${id}`);
  };

  const onDismissNotification = useCallback(
    async (taskId) => {
      try {
        const { data } = await client.query({
          query: NOTIFICATIONS,
          variables: {
            where: { meta: { taskId } },
            take: 1,
          },
          fetchPolicy: "network-only",
        });
        const notification = get(data, "notifications.data.0");
        if (!notification) return;

        const existingReadBy = (notification.isReadBy || []).map((u) => ({
          id: u.id,
        }));
        const alreadyRead = existingReadBy.some((u) => u.id === user?.id);
        if (alreadyRead) return;

        await client.mutate({
          mutation: UPDATE_NOTIFICATION,
          variables: {
            where: { id: notification.id },
            data: {
              isReadBy: [...existingReadBy, { id: user?.id }],
            },
          },
          refetchQueries: [
            { query: UNREAD_NOTIFICATIONS_COUNT },
            {
              query: NOTIFICATIONS,
              variables: {
                where: { type: NOTIFICATION_TYPES.TASK_REMINDER },
                take: 1000,
              },
            },
          ],
        });
      } catch (_err) {
        // silent fail
      }
    },
    [user?.id],
  );

  const onCancelReminder = useCallback(async (taskId) => {
    try {
      await client.mutate({
        mutation: CANCEL_TASK_REMINDER,
        variables: { where: { id: taskId } },
        refetchQueries: [
          { query: TASKS },
          { query: UNREAD_NOTIFICATIONS_COUNT },
        ],
      });
    } catch (_err) {
      // silent fail
    }
  }, []);

  const onReactivateReminder = useCallback(async (taskId) => {
    try {
      await client.mutate({
        mutation: REACTIVATE_TASK_REMINDER,
        variables: { where: { id: taskId } },
        refetchQueries: [
          { query: TASKS },
          { query: UNREAD_NOTIFICATIONS_COUNT },
        ],
      });
    } catch (_err) {
      // silent fail
    }
  }, []);

  return (
    <>
      {!!openDetail && (
        <TaskDetailsDrawer
          open={!!openDetail}
          onClose={onClose}
          task={openDetail}
        />
      )}
      <View
        filter={filter}
        pagination={pagination}
        setPagination={setPagination}
        onSearch={onSearch}
        onDateSearch={onDateSearch}
        onDelete={onDelete}
        onOpenDetail={onOpenDetail}
        onDismissNotification={onDismissNotification}
        onCancelReminder={onCancelReminder}
        onReactivateReminder={onReactivateReminder}
        unreadReminderTaskIds={unreadReminderTaskIds}
        totalCount={totalCount}
        automaticCount={automaticCount}
        toDoCount={toDoCount}
        completedCount={completedCount}
        notAssignedCount={notAssignedCount}
      />
    </>
  );
};

export default List;
